import { HttpError, isRequestCancellation, NetworkError } from '@luminovo/api-contracts';
import { isChunkLoadError } from '@luminovo/commons';
import type { ErrorEvent, EventHint } from '@sentry/react';

// Capture paths: `logToExternalErrorHandlers`, `Sentry.ErrorBoundary` (before its fallback runs) and `globalHandlers`.
/** The only gate every event passes, so a callsite that declines to report can't stop an event; only this can. */
export function beforeSend(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
    const error = hint.originalException;
    const chain = causeChain(error);

    // What the alert rules used to exclude after the fact: about 16,000 of the 30,000 events a month.
    if (chain.some(isRequestCancellation) || chain.some(isConnectivityFailure) || chain.some(isSessionExpiry)) {
        // Still logged by the code that throws it, so the rate stays queryable.
        return null;
    }

    return describeHttpError(event, chain.find((link) => link instanceof HttpError) ?? error);
}

/** The error and its `.cause`s, bounded and cycle-safe, so a predicate sees the failure inside a wrapper. */
function causeChain(error: unknown): unknown[] {
    const chain: unknown[] = [];
    const seen = new Set<unknown>();
    let current: unknown = error;

    for (let depth = 0; depth < CAUSE_CHAIN_MAX_DEPTH && current != null && !seen.has(current); depth++) {
        chain.push(current);
        seen.add(current);
        current = current instanceof Error ? current.cause : undefined;
    }

    return chain;
}

const CAUSE_CHAIN_MAX_DEPTH = 5;

/** Flaky wifi, ad blockers, a tab navigating away: ~7,900 events a month that say nothing about our code. */
function isConnectivityFailure(error: unknown): boolean {
    if (error instanceof NetworkError) {
        return true;
    }
    // Before the message match: a chunk a deploy removed also fails to fetch, and we want that stale-asset signal.
    if (isChunkLoadError(error)) {
        return false;
    }
    const message = error instanceof Error ? error.message : '';
    return CONNECTIVITY_FAILURE_MESSAGES.some((failure) => message.includes(failure));
}

const CONNECTIVITY_FAILURE_MESSAGES = [
    // Chromium. Often with the tenant subdomain appended, so this has to be a substring match.
    'Failed to fetch',
    // Firefox
    'NetworkError when attempting to fetch resource',
    // Safari
    'The Internet connection appears to be offline',
    // Ours: `NetworkError`'s text, for a wrapper that copied the message without setting `{ cause }`.
    'Network connection issue',
];

// The token is refreshed or the user sent to log in, and `http.error` has already logged it with the endpoint.
/** An expired token, the largest noise source: 7,020 events from 207 users in 30 days, across a dozen endpoints. */
function isSessionExpiry(error: unknown): boolean {
    // A 401 with a code, such as `rfq.unauthorized`, is a deliberate refusal on a valid token, so it's kept.
    return error instanceof HttpError && error.status === 401 && error.code === 'unknown';
}

/** Tags the HTTP failure, so an alert rule can filter on a tag instead of matching the message text. */
function describeHttpError(event: ErrorEvent, error: unknown): ErrorEvent {
    if (!(error instanceof HttpError)) {
        return event;
    }

    // A 4xx with a code is a refusal the UI explains; one without is a missing reason, found by `http.expected: false`.
    const expected = isClientError(error.status) && error.code !== 'unknown';

    return {
        ...event,
        // A refusal the API is entitled to give isn't a crash, and level `error` on everything filters nothing.
        level: expected ? 'warning' : event.level,
        // The automatic capture paths set no fingerprint; this groups their events by endpoint too.
        fingerprint: event.fingerprint ?? error.getSentryFingerprint(),
        tags: {
            ...event.tags,
            'http.endpoint': error.endpoint,
            'http.status': error.status,
            'http.error_code': error.code,
            'http.expected': expected,
        },
    };
}

function isClientError(status: number): boolean {
    return status >= 400 && status < 500;
}
